export type BarGraphSize = 'small' | 'medium' | 'large';

/**
 * Identifies which concrete bar graph is being rendered. Each value maps to a fixed
 * presentation config in WIDGET_BAR_GRAPH_CONFIG - add a new value + config entry here
 * to support a future widget instead of adding more @Inputs to the component.
 */
export enum WidgetBarGraphType {
  LocationRebateCompliance = 'location-rebate-compliance',
  LocationMplCompliance = 'location-mpl-compliance',
  LocationSplCompliance = 'location-spl-compliance',
  OverviewRebate = 'overview-rebate',
  OverviewMpl = 'overview-mpl',
  OverviewSpl = 'overview-spl',
  OverviewAll = 'overview-all',
}

export interface WidgetBarGraphConfig {
  barColorClass: string;
  trackColorClass: string;
  targetColorClass: string;
  textColorClass: string;
  size: BarGraphSize;
  widthClass: string;
  heightClass: string;
  showDisplayText: boolean;
  showSupportingText: boolean;
  showMinMaxLabels: boolean;
  showTargetLine: boolean;
  minLabel: string;
  /** Used when the caller does not pass an explicit ariaLabel. */
  defaultAriaLabel: string;
  widgetType?: string;
}

const LOCATION_COMPLIANCE_BASE: Omit<WidgetBarGraphConfig, 'barColorClass' | 'showTargetLine' | 'defaultAriaLabel'> = {
  trackColorClass: 'bg-neptune-disabled',
  targetColorClass: 'bg-black',
  textColorClass: 'text-gray-900',
  size: 'small',
  widthClass: 'w-[90px]',
  heightClass: 'h-4',
  showDisplayText: true,
  showSupportingText: false,
  showMinMaxLabels: false,
  minLabel: '0',
};

const OVERVIEW_BASE: Omit<WidgetBarGraphConfig, 'barColorClass' | 'showTargetLine' | 'defaultAriaLabel'> = {
  trackColorClass: 'bg-neptune-subtle',
  targetColorClass: 'bg-black',
  textColorClass: 'text-gray-900',
  size: 'small',
  widthClass: 'w-full',
  heightClass: 'h-[19px]',
  showDisplayText: false,
  showSupportingText: false,
  showMinMaxLabels: true,
  minLabel: '0',
};

/** Resolver map - the single source of truth for every bar graph's presentation config. */
export const WIDGET_BAR_GRAPH_CONFIG: Record<WidgetBarGraphType, WidgetBarGraphConfig> = {
  [WidgetBarGraphType.LocationRebateCompliance]: {
    ...LOCATION_COMPLIANCE_BASE,
    barColorClass: 'bg-[#813b9e]',
    showTargetLine: true,
    defaultAriaLabel: 'Rebate compliance',
    widgetType: 'location-rebate-compliance',
  },
  [WidgetBarGraphType.LocationMplCompliance]: {
    ...LOCATION_COMPLIANCE_BASE,
    barColorClass: 'bg-blue',
    showTargetLine: false,
    defaultAriaLabel: 'mpl compliance',
    widgetType: 'location-mpl-compliance',
  },
  [WidgetBarGraphType.LocationSplCompliance]: {
    ...LOCATION_COMPLIANCE_BASE,
    barColorClass: 'bg-neptune-color-secondary-800',
    showTargetLine: false,
    defaultAriaLabel: 'spl compliance',
    widgetType: 'location-spl-compliance',
  },
  [WidgetBarGraphType.OverviewRebate]: {
    ...OVERVIEW_BASE,
    barColorClass: 'bg-[#813b9e]',
    showTargetLine: true,
    defaultAriaLabel: 'Rebate Location',
    widgetType: 'overview-rebate',
  },
  [WidgetBarGraphType.OverviewMpl]: {
    ...OVERVIEW_BASE,
    barColorClass: 'bg-blue',
    showTargetLine: false,
    defaultAriaLabel: 'Average MPL Solution Purchased',
    widgetType: 'overview-mpl',
  },
  [WidgetBarGraphType.OverviewSpl]: {
    ...OVERVIEW_BASE,
    barColorClass: 'bg-neptune-color-secondary-800',
    showTargetLine: false,
    defaultAriaLabel: 'Average SPL Solution Purchased',
    widgetType: 'overview-spl',
  },
  [WidgetBarGraphType.OverviewAll]: {
    ...OVERVIEW_BASE,
    barColorClass: 'bg-[#39B54A]',
    showTargetLine: false,
    defaultAriaLabel: 'Average Solution Purchased',
    widgetType: 'overview-all',
  },
};

/** Resolver - looks up the presentation config for a given widget type. */
export function resolveWidgetBarGraphConfig(widgetType: WidgetBarGraphType): WidgetBarGraphConfig {
  return WIDGET_BAR_GRAPH_CONFIG[widgetType];
}
